import React, { Fragment, useEffect, useState } from 'react';
import { keys, reverse, values, isEmpty } from 'lodash';
import { arrayOf, object, string } from 'prop-types';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import 'react-dropdown-tree-select/dist/styles.css';
import DataExport from '../../../../ui-components/data-export/data-export.jsx';
import TableGrid from '../../../../ui-components/table-grid/table-grid.jsx';
import TabsContainer from '../../../../ui-components/tabs-container/tabs-container.jsx';
import {
   CATEGORY,
   PERCENTAGE_ABSOLUTE_TABS_VALUE,
} from '../../../../utils/constants.js';
import { DateUtil } from '../../../../utils/date-util.js';
import {
   computeGridData,
   generateDatesBetween,
   getFromToDate,
   getReasonsDropdownFilteredList,
   isSelectedApprovalStatus,
   RECOMMENDATION_STATUS_KEYS,
   RECOMMENDATION_STATUS_TABS,
   setAndGetReasonsFilterChangeData,
} from '../charts-util.js';
import PercentageAbsoluteTabs from '../percentage-absolute-tabs.jsx';
import { getPercentageAbsoluteTabs } from '../percentage-absolute-util.js';
import { getCompetitiveChangesFareChartTacticalConfig } from './grid-recommendation-tactical-config.js';

const GridRecommendationTactical = ({
   searchCriteriaPayload,
   recommendationStatus,
   recommendationTacticalSummaryData,
   filterBy,
   posOnClickHandler,
   reasonsFilter,
}) => {
   const [reasonsDropdownData, setReasonsDropdownData] = useState([]);
   const [percentAbsolute, setPercentAbsolute] = useState(
      PERCENTAGE_ABSOLUTE_TABS_VALUE.PERCENTAGE
   );
   const fromTo = getFromToDate(searchCriteriaPayload);
   const datesInSequence = generateDatesBetween(fromTo, true);
   const computedGridData = computeGridData(
      recommendationTacticalSummaryData,
      recommendationStatus,
      datesInSequence,
      filterBy,
      percentAbsolute,
      reasonsDropdownData
   );
   const handlePosOnClick = (pos) => {
      posOnClickHandler(pos, reasonsDropdownData);
   };

   const tabGenerator = (computedGridDataNew) => {
      const competitiveChangesFareChartTacticalConfig =
         getCompetitiveChangesFareChartTacticalConfig(
            recommendationStatus,
            reasonsDropdownData,
            datesInSequence,
            filterBy,
            percentAbsolute,
            handlePosOnClick
         );
      const computedGridDataNewKeys = reverse(keys(computedGridDataNew));
      return computedGridDataNewKeys.map((value, key, index) => ({
         tabTitle: DateUtil.formatDate(
            value,
            DateUtil.DATE_FORMATS.DAY_HYPHEN_MONTH_NAME
         ),
         tabContent: (
            <TableGrid
               key={DateUtil.getNowMoment()} //TODO: to implement in a better way
               gridKey={DateUtil.getNowMoment()} //TODO: to implement in a better way
               data={computedGridDataNew[value]}
               config={competitiveChangesFareChartTacticalConfig}
            />
         ),
      }));
   };

   const initialTabContent = tabGenerator(computedGridData);
   const [tabContent, setTabContent] = useState(initialTabContent);
   const percentageAbsoluteTabConfig = getPercentageAbsoluteTabs(
      CATEGORY.TACTICAL
   );

   const handlePercentageAbsoluteTabOnClick = (selectedValue) => {
      setPercentAbsolute(selectedValue);
      const newTabContent = tabGenerator(
         computeGridData(
            recommendationTacticalSummaryData,
            recommendationStatus,
            datesInSequence,
            filterBy,
            selectedValue,
            reasonsDropdownData
         )
      );
      setTabContent([...newTabContent]);
   };

   const handleReasonsFilterOnChange = (currentNode, selectedNodes) => {
      const computedReasonsDropdownData = setAndGetReasonsFilterChangeData(
         reasonsDropdownData,
         currentNode,
         selectedNodes
      );
      const newTabContent = tabGenerator(
         computeGridData(
            recommendationTacticalSummaryData,
            recommendationStatus,
            datesInSequence,
            filterBy,
            percentAbsolute,
            computedReasonsDropdownData
         )
      );
      setTabContent([...newTabContent]);
      setReasonsDropdownData(computedReasonsDropdownData);
   };

   useEffect(() => {
      setReasonsDropdownData(
         getReasonsDropdownFilteredList(
            recommendationStatus,
            recommendationTacticalSummaryData
         )
      );
      const newTabContent = tabGenerator(
         computeGridData(
            recommendationTacticalSummaryData,
            recommendationStatus,
            datesInSequence,
            filterBy,
            percentAbsolute,
            reasonsDropdownData
         )
      );
      setTabContent([...newTabContent]);
   }, [recommendationTacticalSummaryData]);

   useEffect(() => {
      setReasonsDropdownData(
         getReasonsDropdownFilteredList(
            recommendationStatus,
            recommendationTacticalSummaryData
         )
      );
      const newTabContent = tabGenerator(
         computeGridData(
            recommendationTacticalSummaryData,
            recommendationStatus,
            datesInSequence,
            filterBy,
            percentAbsolute,
            reasonsDropdownData
         )
      );
      setTabContent([...newTabContent]);
   }, [filterBy]);

   useEffect(() => {
      const newTabContent = tabGenerator(
         computeGridData(
            recommendationTacticalSummaryData,
            recommendationStatus,
            datesInSequence,
            filterBy,
            percentAbsolute,
            reasonsDropdownData
         )
      );
      setTabContent([...newTabContent]);
   }, [reasonsDropdownData]);

   useEffect(() => {
      if (
         !isEmpty(reasonsFilter) &&
         !isEmpty(reasonsFilter[CATEGORY.TACTICAL])
      ) {
         setReasonsDropdownData(reasonsFilter[CATEGORY.TACTICAL]);
      }
   }, [reasonsFilter]);

   const isApprovalStatus = isSelectedApprovalStatus(recommendationStatus);
   const tableConfig = getCompetitiveChangesFareChartTacticalConfig(
      recommendationStatus,
      reasonsDropdownData,
      datesInSequence,
      filterBy,
      percentAbsolute,
      handlePosOnClick
   );
   const headers = isApprovalStatus
      ? tableConfig.configColumns
      : [{ key: 'date', label: 'Date' }, ...tableConfig.configColumns];

   return (
      <Fragment>
         <div className='col-xs-12'>
            <h5>TACTICAL - DATA</h5>
         </div>

         <div className='col-xs-12 reason-for-filter-container'>
            {isApprovalStatus ? 'Reason for No Recommendation' : 'Reason'}:
            <DropdownTreeSelect
               texts={{ placeholder: 'Select one or more reasons to filter' }}
               data={reasonsDropdownData}
               onChange={handleReasonsFilterOnChange}
            />
         </div>
         <div className='col-xs-12 fo-spark-mar-t-4'>
            <div className='spark-pull-right'>
               {computedGridData && (
                  <DataExport
                     data={computedGridData}
                     groupedKeys={
                        isApprovalStatus
                           ? ['analyst', 'status', 'pos']
                           : ['region', 'pos']
                     }
                     headers={headers}
                     isStackedData={!isApprovalStatus}
                     type='icon'
                     fileName={`${CATEGORY.TACTICAL}-${RECOMMENDATION_STATUS_TABS[recommendationStatus].LABEL_TEXT}.csv`}
                  />
               )}
            </div>

            <div className='spark-pull-right spark-mar-r-1'>
               {RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS ===
               recommendationStatus ? (
                  <PercentageAbsoluteTabs
                     percentageAbsoluteTabDetails={
                        percentageAbsoluteTabConfig.percentageAbsoluteTabDetails
                     }
                     percentageAbsoluteTabOnClickHandler={
                        handlePercentageAbsoluteTabOnClick
                     }
                  />
               ) : null}
            </div>
         </div>
         <div className='col-xs-12 fo-spark-mar-t-4'>
            {RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS ===
            recommendationStatus ? (
               <TableGrid
                  key={DateUtil.getNowMoment()} //TODO: to implement in a better way
                  gridKey={DateUtil.getNowMoment()} //TODO: to implement in a better way
                  data={values(computedGridData)}
                  config={tableConfig}
               />
            ) : (
               tabContent && (
                  <TabsContainer
                     config={{
                        tabsContainerCss:
                           'competitive-changes-fare-tabs-container-tabs-list',
                        tabs: tabContent,
                        tabOnClick: (e) => {},
                     }}
                  />
               )
            )}
         </div>
      </Fragment>
   );
};

GridRecommendationTactical.propTypes = {
   recommendationStatus: string,
   recommendationTacticalSummaryData: arrayOf(object),
};

export default GridRecommendationTactical;
